import { useEffect, useState } from "react";
import { List, Button, Spin, message, Row, Col, Alert } from "antd";
import { LinkOutlined, DisconnectOutlined } from "@ant-design/icons";
import {
  fetchModuleResources,
  linkModuleResources,
  unlinkModuleResource,
} from "../api/ProjectsAPI";

export default function ModuleTabContent({
  project,
  config,
  moduleName,
  onRefreshData,
  isManager,
  userData,
}) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [localLinked, setLocalLinked] = useState(
    project?.modules?.[config.projectKey] || [],
  );

  const canManage = isManager || project?.owner?.id === userData?.id;

  useEffect(() => {
    setLocalLinked(project?.modules?.[config.projectKey] || []);
  }, [project, config.projectKey]);

  const linkedIds = new Set(localLinked.map((r) => String(r[config.idField])));

  useEffect(() => {
    if (!canManage) {
      return;
    }

    const fetchAvail = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchModuleResources(moduleName);
        const dataKey = config.responseKey || config.payloadKey;
        setAvailable(res?.data?.[dataKey] || []);
      } catch (err) {
        const backendMessage = err.response?.data?.error || err.message;
        setError(
          `Could not load available resources for ${moduleName}: ${backendMessage}`,
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAvail();
  }, [moduleName, config.responseKey, config.payloadKey, canManage]);

  const getAvailableId = (r) =>
    String(r[config.availableIdField || config.idField]);
  const getAvailableDisplay = (r) =>
    String(r[config.availableDisplayField || config.displayField]);

  const unlinkedAvailable = available.filter(
    (r) => !linkedIds.has(getAvailableId(r)),
  );

  const handleLink = async (resource) => {
    const payloadObject = config.transformForLink
      ? config.transformForLink(resource)
      : resource;
    const payload = { [config.payloadKey]: [payloadObject] };

    try {
      await linkModuleResources(project.id, moduleName, payload);
      setLocalLinked((prev) => [...prev, payloadObject]);
      message.success("Resource linked successfully");

      onRefreshData();
    } catch {
      message.error("Link failed");
    }
  };

  const handleUnlink = async (resource) => {
    try {
      await unlinkModuleResource(
        project.id,
        moduleName,
        resource[config.idField],
      );
      setLocalLinked((prev) =>
        prev.filter(
          (r) => String(r[config.idField]) !== String(resource[config.idField]),
        ),
      );
      message.success("Resource unlinked successfully");

      onRefreshData();
    } catch {
      message.error("Unlink failed");
    }
  };

  if (loading)
    return <Spin style={{ display: "block", margin: "20px auto" }} />;

  return (
    <Row gutter={16}>
      {/* 2. Conditionally render the Available Resources column */}
      {canManage && (
        <Col span={12}>
          <h4>Available Resources</h4>
          {error ? (
            <Alert message={error} type="error" showIcon />
          ) : (
            <List
              bordered
              size="small"
              dataSource={unlinkedAvailable}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => handleLink(item)}
                    >
                      Link
                    </Button>,
                  ]}
                >
                  {getAvailableDisplay(item)}
                </List.Item>
              )}
            />
          )}
        </Col>
      )}

      {/* 3. Give Linked resources full width (24) if they can't manage, otherwise half (12) */}
      <Col span={canManage ? 12 : 24}>
        <h4>Linked Resources</h4>
        <List
          bordered
          size="small"
          dataSource={localLinked}
          renderItem={(item) => (
            <List.Item
              actions={
                canManage
                  ? [
                      <Button
                        size="small"
                        danger
                        icon={<DisconnectOutlined />}
                        onClick={() => handleUnlink(item)}
                      >
                        Unlink
                      </Button>,
                    ]
                  : []
              }
            >
              {item[config.displayField]}
            </List.Item>
          )}
        />
      </Col>
    </Row>
  );
}
